import { markRaw } from 'vue';
import { chain, differenceBy, groupBy, isEmpty } from 'lodash';
import { WidgetMetadataFactory } from '@/modules/ta/widget/factories/WidgetMetadataFactory';
import { GenericDataModel } from '@/modules/core/app/models/GenericDataModel';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { DrawOption, WidgetType } from '@/modules/ta/widget/widget.constants';
import {
  DataSourceType,
  IoLinkDataSources,
} from '@/modules/core/app/constants/datasource.constants';
import { WidgetFilterSet } from '@/modules/ta/widget/models/metadata/dynamic/filters/WidgetFilterSet';
import { DataGridData } from '@/modules/ta/widget/models/DataGridData';
import { Constant } from '@/modules/core/charts/am5/charts.constants';
import { WidgetContextService } from '@/modules/ta/widget/services/WidgetContextService';
import { PublicShareService } from '@/modules/core/app/services/PublicShareService';

export class Widget extends BaseEntityModel {
  /**
   * @var {number}
   */
  id;

  /**
   * @var {string}
   */
  unique_id;

  /**
   * @var {?string}
   */
  layout_id;

  /**
   * @var {?string}
   */
  report_id;

  /**
   * @var {?string}
   */
  type;

  /**
   * @var {number}
   */
  width;

  /**
   * @var {number}
   */
  height;

  /**
   * @var {number}
   */
  display_order;

  /**
   * @var {?string}
   */
  title;

  /**
   * @var {?object}
   */
  paginate_details;

  /**
   * @var {boolean}
   */
  is_predefined;

  /**
   * @var {boolean}
   */
  is_in_library;

  /**
   * @var {boolean}
   */
  created_from_library;

  /**
   * @var {BaseWidgetMetadata}
   */
  metadata;

  /**
   * @var {datetime}
   */
  created_at;

  /**
   * @var {datetime}
   */
  updated_at;

  /**
   * @var {number}
   */
  library_download_count;

  /**
   * @var {?string}
   */
  data_type;

  /**
   * @var {?string}
   */
  data_source_id;

  /**
   * @var {?string}
   */
  data_source_unique_id;

  /**
   * @var {?string}
   */
  data_view_id;

  /**
   * @var {string}
   */
  type_display;

  /**
   * @var {string}
   */
  formatted_created_at;

  /**
   * @var {string}
   */
  formatted_updated_at;

  /**
   * @var {boolean}
   */
  has_live_integration;

  /**
   * @var {GenericDataModel[]|DataGridData}
   */
  cached_data;

  /**
   * This field only exist at front-end and mainly used to edit filters in widget builder
   * @type {WidgetFilterSet}
   */
  filter_set;

  /**
   * @var {boolean}
   */
  is_using_calculation;

  /**
   * @var {string}
   */
  assignments;

  /**
   * @var {string}
   */
  timegroup;

  /**
   * @var {boolean}
   */
  hasNoData;

  constructor(model = {}) {
    super(model);

    this.id = model.id;
    this.unique_id = model.unique_id;
    this.layout_id = model.layout_id;
    this.report_id = model.report_id;
    this.type = model.type;
    this.width = model.width;
    this.height = model.height;
    this.display_order = model.display_order;
    this.title = model.title ?? '';
    this.paginate_details = model.paginate_details;
    this.is_predefined = model.is_predefined;
    this.is_in_library = model.is_in_library;
    this.created_from_library = model.created_from_library;
    this.metadata = WidgetMetadataFactory.build(model);
    this.created_at = model.created_at;
    this.updated_at = model.updated_at;
    this.library_download_count = model.library_download_count;
    this.data_type = model.data_type ?? model.metadata?.data_source?.type;
    this.data_source_id = model.data_source_id ?? model.metadata?.data_source?.id;
    this.data_source_unique_id =
      model.data_source_unique_id ?? `${this.data_source_id}||${this.data_type}`;
    this.data_view_id = model.data_view_id ?? model.metadata?.data_source?.data_view;
    this.type_display = model.type_display;
    this.formatted_created_at = model.formatted_created_at;
    this.formatted_updated_at = model.formatted_updated_at;
    this.has_live_integration = model.has_live_integration;
    this.assignments = model.assignments;
    this.timegroup = model.timegroup;
    this.is_using_calculation = model.is_using_calculation;
    this.hasNoData = model.hasNoData ?? false;
    this.setFilterSetFromDataSource();
    this.buildCachedData(model.cached_data);
  }

  buildCachedData(data) {
    if (!data) {
      this.cached_data = null;
      return;
    }

    let actualData;
    if (data.aaData) {
      actualData = new DataGridData(data);
    } else {
      actualData = Array.isArray(data)
        ? data.map((datum) => new GenericDataModel(datum))
        : new GenericDataModel(data);
    }

    this.cached_data = markRaw(actualData);
  }

  /**
   * @returns {boolean}
   */
  isChartWidget() {
    return (
      this.isSliceChartWidget() ||
      this.isSerialChartWidget() ||
      this.isGeoChartWidget() ||
      this.isGaugeChartWidget() ||
      this.isBubbleChartWidget() ||
      this.isSankeyChartWidget() ||
      this.isPivotTableWidget()
    );
  }

  /**
   * @returns {boolean}
   */
  isSankeyChartWidget() {
    return this.type === WidgetType.TYPE_SANKEYCHART;
  }

  /**
   * @returns {boolean}
   */
  isPivotTableWidget() {
    return this.type === WidgetType.TYPE_PIVOTTABLE;
  }

  /**
   * Helper function to tell us if a chart is a slice chart
   * @returns {boolean}
   */
  isSliceChartWidget() {
    return this.type === WidgetType.TYPE_PIECHART || this.type === WidgetType.TYPE_FUNNELCHART;
  }

  /**
   * Helper function to tell us if a chart is a slice chart
   * @returns {boolean}
   */
  isFunnelChart() {
    return this.type === WidgetType.TYPE_FUNNELCHART;
  }

  /**
   * Helper function to tell us if a chart is a pie chart
   * @returns {boolean}
   */
  isPieChart() {
    return this.type === WidgetType.TYPE_PIECHART;
  }

  /**
   * Helper function to tell us if a chart is a serial chart
   * @returns {boolean}
   */
  isSerialChartWidget() {
    return (
      this.type === WidgetType.TYPE_BARCHART ||
      this.type === WidgetType.TYPE_LINECHART ||
      this.type === WidgetType.TYPE_COMBINATIONCHART
    );
  }

  /**
   * Helper function to tell us if a chart is a serial chart
   * @returns {boolean}
   */
  isBubbleChartWidget() {
    return this.type === WidgetType.TYPE_BUBBLECHART;
  }

  /**
   * Helper function to tell us if a chart is a bar chart
   * @returns {boolean}
   */
  isBarChartWidget() {
    return this.type === WidgetType.TYPE_BARCHART;
  }

  /**
   * Helper function to tell us if a chart is a line chart
   * @returns {boolean}
   */
  isLineChartWidget() {
    return this.type === WidgetType.TYPE_LINECHART;
  }

  /**
   * Helper function to tell us if a chart is a combination chart
   * @returns {boolean}
   */
  isCombinationChartWidget() {
    return this.type === WidgetType.TYPE_COMBINATIONCHART;
  }

  /**
   * @returns {boolean}
   */
  isGaugeChartWidget() {
    return this.type === WidgetType.TYPE_GAUGECHART;
  }

  /**
   * @returns {boolean}
   */
  isDataGridWidget() {
    return this.type === WidgetType.TYPE_DATAGRID;
  }

  /**
   * @returns {boolean}
   */
  isPaginatedDataGridWidget() {
    return this.isDataGridWidget() && this.getDrawOptions()[DrawOption.GRID_PAGINATE];
  }

  /**
   * @returns {boolean}
   */
  isGeoChartWidget() {
    return this.type === WidgetType.TYPE_GEOCHART;
  }

  /**
   * @returns {boolean}
   */
  isBigNumberWidget() {
    return this.type === WidgetType.TYPE_BIGNUMBER;
  }

  /**
   * @returns {boolean}
   */
  isAdminWidget() {
    return this.type === WidgetType.TYPE_ACCOUNTMANAGER;
  }

  isChatGPTWidget() {
    return this.type === WidgetType.TYPE_CHATGPT;
  }

  isDataSourcedWidget() {
    return (
      this.isBigNumberWidget() ||
      this.isDataGridWidget() ||
      this.isChartWidget() ||
      this.isGeoChartWidget()
    );
  }

  /**
   * @returns {boolean}
   */
  hasDataSource() {
    return this.isDataSourcedWidget() || (this.isMediaWidget() && this.data_source_id);
  }

  /**
   * @returns {boolean}
   */
  isUsingSampleData() {
    return (
      this.metadata.draw_options[DrawOption.FORCE_SAMPLE_DATA] ||
      this.metadata.draw_options[DrawOption.SHOW_SAMPLE_DATA]
    );
  }

  canFilterData() {
    return (this.has_live_integration || this.isDataSourcedWidget()) && !this.isAdminWidget();
  }

  /**
   * @returns {boolean}
   */
  isMediaWidget() {
    return this.type === WidgetType.TYPE_MEDIA;
  }

  isLeadsWidget() {
    return this.data_type === DataSourceType.LEADS;
  }

  isGoalWidget() {
    return this.data_type === DataSourceType.GOAL_DATA;
  }

  requiresManualRenderedSignal() {
    return this.isMediaWidget() || this.isAdminWidget();
  }

  /**
   * Alerts are only configurable for certain data sources
   * @returns {boolean}
   */
  canShowAlerts() {
    return ![
      DataSourceType.GOAL_DATA,
      DataSourceType.BENCHMARK_DATA,
      DataSourceType.LEADS,
      DataSourceType.USER_LOGINS,
      ...IoLinkDataSources,
    ].includes(this.data_type);
  }

  /**
   * @returns {boolean}
   */
  hasFilters() {
    return !this.isMediaWidget() && !!this.metadata.filter_set_id;
  }

  /**
   * @returns {Array|null}
   */
  getFilters() {
    return this.hasFilters() ? this.metadata.dynamic.filters : null;
  }

  /**
   * @returns {Array.<Alert>}
   */
  getAlerts() {
    return this.metadata?.dynamic?.alerts;
  }

  /**
   * @returns {Array.<Annotation>}
   */
  getAnnotations() {
    return this.metadata?.dynamic?.annotations;
  }

  /**
   * @returns {ApiColumn[]}
   */
  getSelectedColumns() {
    return this.metadata.data_columns.selected;
  }

  getNonGroupedColumns() {
    const { selected = [], grouped = [] } = this.metadata.data_columns;

    // eslint-disable-next-line tap/no-raw-text-js
    return differenceBy(selected, grouped, 'field');
  }

  /**
   * @returns {ApiColumn[]}
   */
  getGroupedColumns() {
    return this.metadata.data_columns.grouped;
  }

  /**
   * @returns {*}
   */
  isMultiGrouped() {
    return this.metadata.is_multi_grouped;
  }

  /**
   * @returns {boolean}
   */
  isDrillDownEnabled() {
    // Public share (permalink) drilldown was not fully dev-tested and is not
    // part of the phase-1 spec — disabled; re-enable once introduced and
    // tested fully.
    return (
      !WidgetContextService.isReportStudioContext() &&
      !PublicShareService.isPublicShareView() &&
      !!(this.getGroupedColumns()?.length > 1 && !this.isMultiGrouped())
    );
  }

  /**
   * @returns {boolean}
   */
  hasDateRangeOverride() {
    return this.metadata.is_overriding_date_range;
  }

  /**
   * @returns {boolean}
   */
  hasVisibleTitle() {
    return !!(
      this.title &&
      // eslint-disable-next-line tap/no-raw-text-js
      !['', '<br>', '<br/>', '<p><br></p>', '<p><br/></p>'].includes(this.title)
    );
  }

  /**
   * @param {ApiColumn} column
   * @returns {boolean}
   */
  hasRoundedMetric(column) {
    return !!this.metadata.rounded_metrics?.includes(column.field);
  }

  /**
   * @returns {boolean}
   */
  canEdit() {
    return !this.is_predefined && this.can_be_edited;
  }

  getDrawOptions() {
    return this.metadata.draw_options;
  }

  /**
   * @returns {string}
   */
  getTimeGrouping() {
    return this.metadata.time_grouping;
  }

  /**
   * @returns {boolean}
   */
  canProbeData() {
    return (
      !this.metadata.is_overriding_date_range &&
      !this.has_live_integration &&
      (this.data_type === DataSourceType.SERVICE_DATA ||
        this.data_type === DataSourceType.CATEGORY_DATA ||
        this.data_type === DataSourceType.LEADS ||
        this.data_type === DataSourceType.GOAL_DATA ||
        this.data_type === DataSourceType.BENCHMARK_DATA ||
        this.data_type === DataSourceType.INDUSTRY_BENCHMARK_PAID_MEDIA ||
        this.data_type === DataSourceType.INDUSTRY_BENCHMARK_EMAIL ||
        this.data_type === DataSourceType.INDUSTRY_BENCHMARK_LEAD_TRACKING ||
        this.data_type === DataSourceType.SEO_WEBSITES)
    );
  }

  /**
   * @param metadata
   * @returns {*|boolean}
   */
  hasMetadataErrors() {
    return (
      this.metadata.dynamic &&
      (!isEmpty(this.metadata.dynamic.warnings) || !isEmpty(this.metadata.dynamic.errors))
    );
  }

  /**
   * Has the required properties to start getting data from the data source
   * @returns {boolean}
   */
  hasDataConfiguration() {
    return !!(
      this.data_source_id &&
      this.data_type &&
      this.data_view_id &&
      this.metadata.data_source
    );
  }

  /**
   * Returns if comparison is enabled at widget level.
   * @returns {*}
   */
  hasComparison() {
    return this.metadata.compare_to_prior_period;
  }

  setFilterSetFromDataSource() {
    const { data_source, dynamic, filter_set_id } = this.metadata;
    if (!data_source) {
      return null;
    }
    this.filter_set = new WidgetFilterSet({
      id: filter_set_id,
      data_source,
      filters: dynamic?.filters,
      name: `${data_source.id_name} ${data_source.data_view_name} filter set`,
    });
  }

  invalidateFilters() {
    this.filter_set?.clearInvalidFilters();
  }

  /**
   * Returns if the widget can support sorting.
   * @returns {boolean}
   */
  canSort() {
    return this.isDataGridWidget() || this.isSerialChartWidget() || this.isBubbleChartWidget();
  }

  appendSparkLineData(data, sparkLineData, usingCachedData = false) {
    const groupedColumns = this.getGroupedColumns().filter(
      (column) => column.field !== Constant.LOG_DATE
    );
    const groupByFields = this.getGroupedColumns().map((column) => column.field);
    if (groupedColumns.length < 1) {
      return;
    }
    const groupedData = groupBy(sparkLineData, (obj) =>
      this.generateGroupKey(obj, groupedColumns, usingCachedData)
    );

    data.aaData.forEach((datum) => {
      let groupByValue = '';
      groupByFields.forEach((groupByField, index) => {
        let currentValue;
        if (usingCachedData) {
          currentValue = datum?.[groupByField] ?? datum?.current_period?.[groupByField];
        } else {
          currentValue = datum.current_period?.[groupByField] || datum.prior_period?.[groupByField];
        }

        if (currentValue) {
          groupByValue += `${currentValue}${index === groupedColumns.length - 1 ? '' : ' - '}`;
        }
      });

      datum.sparkline_data =
        chain(groupedData[groupByValue] ?? [])
          .map((row) => row.current_period || (usingCachedData && row))
          .value() ?? [];
    });
  }

  /**
   * @param rowData
   * @param groupedColumns
   * @param usingCachedData
   * @returns {*}
   */
  generateGroupKey(rowData, groupedColumns, usingCachedData = false) {
    return groupedColumns
      .map((column) => {
        if (usingCachedData) {
          return rowData?.[column.field];
        }

        return (
          (rowData.current_period && rowData.current_period[column.field]) ||
          (rowData.prior_period && rowData.prior_period[column.field])
        );
      })
      .join(' - ');
  }

  /**
   * @returns {boolean}
   */
  isSortableChart() {
    return this.isSerialChartWidget() || this.isBubbleChartWidget();
  }
}
